//@ts-check
import { useState, useRef, useLayoutEffect, useCallback } from "react";
import { useOutsideDrop } from "../../../../hooks/useOutsideDrop";
import Portal from "../../../../components/general/Modal/Portal";
import {
  DropOptionsWrapper,
  DropOptionsButton,
  DropOptionsMenu,
  DropOptionsItem,
  DropOptionsItemContent,
  DropOptionsItemIcon,
  DropOptionsItemLabel,
} from "./styles";

function ThreeDotsIcon({ width = 20, height = 20 }) {
  return (
    <svg width={width} height={height} viewBox="0 0 20 20" fill="none">
      <circle cx="4" cy="10" r="2" fill="#111" />
      <circle cx="10" cy="10" r="2" fill="#111" />
      <circle cx="16" cy="10" r="2" fill="#111" />
    </svg>
  );
}

/**
 * @typedef {Object} DropOption
 * @property {string} label
 * @property {() => void} onClick
 * @property {JSX.Element=} icon
 * @property {boolean} show
 */

/**
 * @param {{ options: Array<DropOption>, data?: any }} props
 */
export function DropOptions({ options, data }) {
  const [open, setOpen] = useState(false);
  /** @type {import('react').RefObject<HTMLDivElement>} */
  const ref = useRef(null);
  /** @type {import('react').RefObject<HTMLButtonElement>} */
  const buttonRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });

  useOutsideDrop(buttonRef, () => setOpen(false));

  const handleOptionClick = useCallback(
    (onClick) => {
      setOpen(false);
      if (data) onClick(data);
      onClick();
    },
    [data]
  );
  useLayoutEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 160;
      const rightEdge = rect.right + window.scrollX;
      const left = Math.max(rightEdge - menuWidth, 8);
      const maxLeft = window.innerWidth - menuWidth - 8;
      setMenuPos({
        top: rect.bottom + window.scrollY - 8,
        left: Math.min(left, maxLeft),
        width: rect.width,
      });
    }
  }, [open]);

  return (
    <DropOptionsWrapper>
      <DropOptionsButton
        ref={buttonRef}
        onClick={() => {
          if (options.length) setOpen((v) => !v);
        }}
        aria-label="Opções"
      >
        <ThreeDotsIcon />
      </DropOptionsButton>
      {open && menuPos.top !== 0 && menuPos.left !== 0 && (
        <Portal selector="body">
          <DropOptionsMenu
            ref={ref}
            style={{
              position: "absolute",
              top: menuPos.top,
              left: menuPos.left,
              minWidth: 160,
              zIndex: 9999,
            }}
          >
            {options.map((opt, idx) => (
              <DropOptionsItem
                key={idx}
                isLast={idx === options.length - 1}
                onClick={() => handleOptionClick(opt.onClick)}
                onMouseDown={(e) => e.preventDefault()}
              >
                <DropOptionsItemContent>
                  {opt.icon && (
                    <DropOptionsItemIcon>{opt.icon}</DropOptionsItemIcon>
                  )}
                  <DropOptionsItemLabel>{opt.label}</DropOptionsItemLabel>
                </DropOptionsItemContent>
              </DropOptionsItem>
            ))}
          </DropOptionsMenu>
        </Portal>
      )}
    </DropOptionsWrapper>
  );
}
